import React, { memo, useCallback, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  FormControl,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import { AddRounded, BusinessOutlined, FolderOutlined, Search } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { formatDate } from '../utils/dateUtils';
import { useApp } from '../context/AppContext';
import { customerPath, openDocumentEditor, projectPath } from '../utils/navigation';
import DocumentPreviewDialog from './DocumentPreviewDialog';
import useDocumentUpdatedListener from './editor/useDocumentUpdatedListener';
import { ConfirmDialog, EmptyState, ListSkeleton, PageContainer, PageHeader, SectionCard } from './pagekit';
import DocumentCard from './documents/DocumentCard';
import DocumentActionsMenu from './documents/DocumentActionsMenu';
import useDocumentExport from './documents/useDocumentExport';
import { buildDocumentSnippet, isPresentationDocument, summarizeFieldStatus } from './documents/documentMeta';

const UNKNOWN_TEMPLATE = 'Other documents';

/**
 * Lowercases a value for search; handles null/undefined without throwing.
 * @param {unknown} value - Raw field from document or project.
 * @returns {string} Lowercase string, or empty when absent.
 */
function toSearchString(value) {
  if (value == null) return '';
  return String(value).toLowerCase();
}

/**
 * All generated documents across the user's projects, grouped by template, with search,
 * template/customer filters, preview, export and delete.
 * @returns {JSX.Element} Page.
 */
const DocumentList = memo(() => {
  const navigate = useNavigate();
  const { state, actions } = useApp();
  const { exporting, exportDocument } = useDocumentExport();

  const [menuAnchorEl, setMenuAnchorEl] = useState(null);
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterTemplate, setFilterTemplate] = useState('all');
  const [filterCustomer, setFilterCustomer] = useState('all');
  const [documentToPreview, setDocumentToPreview] = useState(null);
  const [documentToDelete, setDocumentToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);

  // Every document from every project, newest first, with its card data computed once.
  const allDocuments = useMemo(() => {
    const docs = [];
    state.projects.forEach((project) => {
      (project.generatedDocuments || []).forEach((doc) => {
        docs.push({
          ...doc,
          projectId: project.id,
          projectName: project.name,
          customerName: project.customer?.name || 'Unknown customer',
          customerId: project.customer_id,
          snippet: buildDocumentSnippet(doc.content),
          fieldSummary: summarizeFieldStatus(doc),
          isPresentation: isPresentationDocument(doc, state.templates),
        });
      });
    });
    return docs.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
  }, [state.projects, state.templates]);

  const filteredDocuments = useMemo(() => {
    const queryLower = searchQuery.trim().toLowerCase();
    return allDocuments.filter((doc) => {
      const matchesSearch =
        queryLower === '' ||
        toSearchString(doc.name).includes(queryLower) ||
        toSearchString(doc.templateName).includes(queryLower) ||
        toSearchString(doc.projectName).includes(queryLower) ||
        toSearchString(doc.customerName).includes(queryLower);
      const matchesTemplate = filterTemplate === 'all' || doc.templateId === filterTemplate;
      const matchesCustomer = filterCustomer === 'all' || doc.customerId === filterCustomer;
      return matchesSearch && matchesTemplate && matchesCustomer;
    });
  }, [allDocuments, searchQuery, filterTemplate, filterCustomer]);

  const groupedDocuments = useMemo(() => {
    const groups = new Map();
    filteredDocuments.forEach((doc) => {
      const key = doc.templateName || UNKNOWN_TEMPLATE;
      if (!groups.has(key)) groups.set(key, []);
      groups.get(key).push(doc);
    });
    return Array.from(groups.entries());
  }, [filteredDocuments]);

  const uniqueTemplates = useMemo(() => {
    const map = new Map();
    allDocuments.forEach((doc) => {
      if (doc.templateId && !map.has(doc.templateId)) map.set(doc.templateId, doc.templateName || 'Untitled template');
    });
    return Array.from(map, ([id, name]) => ({ id, name })).sort((a, b) => a.name.localeCompare(b.name));
  }, [allDocuments]);

  const uniqueCustomers = useMemo(() => {
    const map = new Map();
    allDocuments.forEach((doc) => {
      if (doc.customerId && !map.has(doc.customerId)) map.set(doc.customerId, doc.customerName);
    });
    return Array.from(map, ([id, name]) => ({ id, name })).sort((a, b) => a.name.localeCompare(b.name));
  }, [allDocuments]);

  // Editor tabs save to the database themselves; only refresh the list when they report a save.
  useDocumentUpdatedListener((updates) => {
    if (typeof actions.patchGeneratedDocumentLocal === 'function') {
      updates.forEach((u) => actions.patchGeneratedDocumentLocal(u.documentId, {
        content: u.content,
        updatedAt: u.updatedAt,
        ...(u.pageSetup !== undefined ? { pageSetup: u.pageSetup } : {}),
      }));
    } else {
      actions.loadProjects();
    }
  });

  const handleMenuClose = useCallback(() => {
    setMenuAnchorEl(null);
    setSelectedDocument(null);
  }, []);

  /**
   * Run a menu action against the selected document, then close the menu.
   * @param {function(object): void} fn - Action.
   * @returns {void}
   */
  const runMenuAction = (fn) => {
    const doc = selectedDocument;
    handleMenuClose();
    if (doc) fn(doc);
  };

  const handleConfirmDelete = async () => {
    if (!documentToDelete) return;
    setDeleting(true);
    try {
      await actions.deleteGeneratedDocument(documentToDelete.projectId, documentToDelete.id);
    } finally {
      setDeleting(false);
      setDocumentToDelete(null);
    }
  };

  const hasFilters = searchQuery !== '' || filterTemplate !== 'all' || filterCustomer !== 'all';
  const clearFilters = () => {
    setSearchQuery('');
    setFilterTemplate('all');
    setFilterCustomer('all');
  };
  const loading = state.loading || !state.initialLoadComplete;

  return (
    <PageContainer>
      <PageHeader
        title="Documents"
        description="Everything generated from your templates, across all projects."
        actions={(
          <Button variant="contained" startIcon={<AddRounded />} onClick={() => navigate('/generate')}>
            Generate document
          </Button>
        )}
      />

      {allDocuments.length > 0 && (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1 }}>
          <TextField
            placeholder="Search documents, templates, projects"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            sx={{ flex: '1 1 260px', maxWidth: { md: 420 } }}
            inputProps={{ 'aria-label': 'Search documents' }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search fontSize="small" />
                </InputAdornment>
              ),
            }}
          />
          <FormControl sx={{ flex: '1 1 180px', maxWidth: { sm: 240 } }}>
            <InputLabel id="filter-template-label">Template</InputLabel>
            <Select
              labelId="filter-template-label"
              id="filter-template-select"
              value={filterTemplate}
              onChange={(e) => setFilterTemplate(e.target.value)}
              label="Template"
            >
              <MenuItem value="all">All templates</MenuItem>
              {uniqueTemplates.map((template) => (
                <MenuItem key={template.id} value={template.id}>{template.name}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl sx={{ flex: '1 1 180px', maxWidth: { sm: 240 } }}>
            <InputLabel id="filter-customer-label">Customer</InputLabel>
            <Select
              labelId="filter-customer-label"
              id="filter-customer-select"
              value={filterCustomer}
              onChange={(e) => setFilterCustomer(e.target.value)}
              label="Customer"
            >
              <MenuItem value="all">All customers</MenuItem>
              {uniqueCustomers.map((customer) => (
                <MenuItem key={customer.id} value={customer.id}>{customer.name}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <Typography variant="body2" color="text.secondary" sx={{ ml: { md: 'auto' } }} aria-live="polite">
            {hasFilters
              ? `${filteredDocuments.length} of ${allDocuments.length} documents`
              : `${allDocuments.length} document${allDocuments.length === 1 ? '' : 's'}`}
          </Typography>
        </Box>
      )}

      {loading && allDocuments.length === 0 ? (
        <ListSkeleton variant="cards" rows={6} label="Loading documents" />
      ) : allDocuments.length === 0 ? (
        <SectionCard>
          <EmptyState
            inky
            title="No documents yet"
            description="Choose a template and a project, and Inky fills it from the project's requirements and sessions."
            actions={(
              <>
                <Button variant="contained" onClick={() => navigate('/generate')}>Generate a document</Button>
                <Button variant="outlined" onClick={() => navigate('/templates')}>Browse templates</Button>
              </>
            )}
          />
        </SectionCard>
      ) : filteredDocuments.length === 0 ? (
        <SectionCard>
          <EmptyState
            icon={<Search />}
            title="No documents match"
            description="Try a different search or clear the filters."
            actions={<Button variant="outlined" onClick={clearFilters}>Clear filters</Button>}
          />
        </SectionCard>
      ) : (
        groupedDocuments.map(([templateName, docs]) => (
          <Box component="section" key={templateName} aria-label={templateName} sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, minWidth: 0, mt: 0.5 }}>
              <Typography component="h2" sx={{ fontSize: 15, fontWeight: 600, overflowWrap: 'anywhere' }}>
                {templateName}
              </Typography>
              <Typography variant="body2" color="text.secondary">{docs.length}</Typography>
            </Box>
            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(min(100%, 300px), 1fr))', gap: 2 }}>
              {docs.map((doc) => (
                <DocumentCard
                  key={doc.id}
                  document={doc}
                  snippet={doc.snippet}
                  fieldSummary={doc.fieldSummary}
                  isPresentation={doc.isPresentation}
                  metaLine={`${formatDate(doc.createdAt)} · ${doc.requirementsCount || 0} requirement${doc.requirementsCount === 1 ? '' : 's'}`}
                  busyLabel={exporting?.id === doc.id ? `Exporting ${exporting.type.toUpperCase()}` : null}
                  onOpen={() => setDocumentToPreview(doc)}
                  onMenuOpen={(event) => {
                    setMenuAnchorEl(event.currentTarget);
                    setSelectedDocument(doc);
                  }}
                  chips={(
                    <>
                      <Chip
                        size="small"
                        variant="outlined"
                        icon={<BusinessOutlined />}
                        label={doc.customerName}
                        onClick={doc.customerId ? (event) => {
                          event.stopPropagation();
                          navigate(customerPath(doc.customerId));
                        } : undefined}
                        sx={{ maxWidth: '100%' }}
                      />
                      <Chip
                        size="small"
                        variant="outlined"
                        icon={<FolderOutlined />}
                        label={doc.projectName}
                        onClick={(event) => {
                          event.stopPropagation();
                          navigate(projectPath(doc.projectId, 'documents'));
                        }}
                        sx={{ maxWidth: '100%' }}
                      />
                    </>
                  )}
                />
              ))}
            </Box>
          </Box>
        ))
      )}

      <DocumentActionsMenu
        anchorEl={menuAnchorEl}
        isPresentation={Boolean(selectedDocument?.isPresentation)}
        exportDisabled={Boolean(exporting)}
        onClose={handleMenuClose}
        onPreview={() => runMenuAction(setDocumentToPreview)}
        onEdit={() => runMenuAction((doc) => openDocumentEditor(navigate, { documentId: doc.id, projectId: doc.projectId, title: doc.name }))}
        onOpenNewTab={() => runMenuAction((doc) => openDocumentEditor(navigate, { documentId: doc.id, projectId: doc.projectId, newTab: true, title: doc.name }))}
        onExport={(type) => runMenuAction((doc) => exportDocument(doc, type, doc.projectName))}
        onDelete={() => runMenuAction(setDocumentToDelete)}
      />

      {documentToPreview && (
        <DocumentPreviewDialog
          open
          onClose={() => setDocumentToPreview(null)}
          title={documentToPreview.name}
          subtitle={`${documentToPreview.projectName} · ${formatDate(documentToPreview.createdAt)}`}
          content={documentToPreview.content}
          actions={(
            <Button
              variant="contained"
              onClick={() => {
                const doc = documentToPreview;
                setDocumentToPreview(null);
                openDocumentEditor(navigate, { documentId: doc.id, projectId: doc.projectId, title: doc.name });
              }}
            >
              Open in editor
            </Button>
          )}
        />
      )}

      <ConfirmDialog
        open={Boolean(documentToDelete)}
        title="Delete document?"
        message={`"${documentToDelete?.name || ''}" will be permanently deleted. This cannot be undone.`}
        busy={deleting}
        onConfirm={handleConfirmDelete}
        onClose={() => setDocumentToDelete(null)}
      />
    </PageContainer>
  );
});

DocumentList.displayName = 'DocumentList';

export default DocumentList;
